import React, { useState, useRef, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { messageBoxHideAction } from 'src/reducers/message-box/action'
import { Fade, withStyles } from '@material-ui/core'
import { MessageBoxHeader, MessageBoxButtons } from './components'
import styles from './styles'
import clsx from 'clsx'

const MessageBox = props => {
  const { classes, content, id } = props
  const dispatch = useDispatch()
  const isDraggable = content.draggable
  const [isDragging, setIsDragging] = useState(false)
  const [position, setPosition] = useState(null) // Start with null position
  const [initialPosition, setInitialPosition] = useState({ x: 0, y: 0 })
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const boxRef = useRef(null)
  // Calculate initial position before first render
  useEffect(() => {
    if (boxRef.current && isDraggable && !position) {
      const box = boxRef.current.getBoundingClientRect()
      const centerX = (window.innerWidth - box.width) / 2
      const centerY = (window.innerHeight - box.height) / 2
      setPosition({ x: centerX, y: centerY })
      setInitialPosition({ x: centerX, y: centerY })
    }
  }, [isDraggable, position]) // Add position to dependencies

  const onClose = () => {
    content.callback && content.callback()
    setPosition(null)
    dispatch(messageBoxHideAction(`message-box-${id}`))
  }

  const handleMouseDown = e => {
    if (!isDraggable) return
    if (e.target.closest('.message-box-header')) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - (position?.x || 0),
        y: e.clientY - (position?.y || 0),
      })
    }
  }

  const handleMouseMove = e => {
    if (isDragging && isDraggable && position) {
      const newX = e.clientX - dragStart.x
      const newY = e.clientY - dragStart.y

      const box = boxRef.current.getBoundingClientRect()
      const maxX = window.innerWidth - box.width
      const maxY = window.innerHeight - box.height

      setPosition({
        x: Math.min(Math.max(0, newX), maxX),
        y: Math.min(Math.max(0, newY), maxY),
      })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  // Don't render the component until we have a position
  const boxStyles = isDraggable
    ? {
        position: 'fixed',
        left: 0,
        top: 0,
        transform: position
          ? `translate(${position.x}px, ${position.y}px)`
          : 'none',
        userSelect: 'none',
        margin: 0,
        visibility: position ? 'visible' : 'hidden', // Hide until positioned
        maxHeight: content.maxHeight ? content.maxHeight : 'unset',
        height: content.maxHeight ? '100%' : 'unset',
      }
    : {
        position: 'relative',
        maxWidth: content.maxWidth ? content.maxWidth : 'fit-content',
        width: '100%',
        minWidth: '400px',
        maxHeight: content.maxHeight ? content.maxHeight : 'unset',
        height: content.maxHeight ? '100%' : 'unset',
        overflow: content.maxHeight ? 'auto' : 'hidden',
      }

  useEffect(() => {
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])
  const hideCloseButton = content.hideCloseButton

  return (
    <>
      {content && (
        <Fade in={true} mountOnEnter unmountOnExit>
          <div
            className={clsx({
              [classes.overlay]: true,
              [classes.draggable]: isDraggable,
            })}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            id={`message-box-${id}`}
          >
            <div
              ref={boxRef}
              className={clsx({
                [classes.boxDraggable]: isDraggable === true,
                [classes.box]: isDraggable !== true,
              })}
              onMouseDown={handleMouseDown}
              style={boxStyles}
            >
              <div
                className="message-box-header"
                style={{
                  cursor: isDraggable ? 'grab' : 'default',
                }}
              >
                {isDraggable === true && <div className={classes.dragLine} />}

                <MessageBoxHeader
                  title={content.title}
                  icon={content.type === 0 && '/assets/delete-icon.svg'}
                  onClose={onClose}
                  isDraggable={isDraggable}
                  content={content}
                  isDragging={isDragging}
                  hideCloseButton={hideCloseButton}
                />
              </div>

              {content.subTitle && (
                <div className={classes.subTitleStyle}>{content.subTitle}</div>
              )}
              {content.description && (
                <div className={classes.descriptionStyle}>
                  {content.description}
                </div>
              )}
              {content.note && (
                <div className={classes.noteStyle}>{content.note}</div>
              )}
              {content.component && <>{content.component}</>}
              {content.buttons?.length !== 0 && (
                <MessageBoxButtons buttons={content.buttons} />
              )}
            </div>
          </div>
        </Fade>
      )}
    </>
  )
}

export default withStyles(styles)(MessageBox)
